import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Save, ArrowLeft, Plus, Trash2, Calendar, 
  Activity, FileText, Pill, Loader, AlertCircle 
} from 'lucide-react';
import apiService from '../services/api';

const VisitEditForm = () => {
  const { id: patientId, visitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initial State matching your backend requirements
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    symptoms: '',
    diagnosis: '',
    prescriptions: '',
    notes: '',
    vitals: {
      bloodPressure: '',
      temperature: '',
      weight: '',
      height: ''
    },
    treatments: [] // Array of { name: '', fee: '' }
  });

  useEffect(() => {
    loadVisitData();
  }, [visitId]);

  const loadVisitData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getVisit(visitId); // This calls getById in PHP
      
      // We need to map the backend FLAT structure to our frontend NESTED structure
      // Backend returns: visit_date, blood_pressure, temperature, etc.
      // Backend expects: date, vitals: { bloodPressure, temperature... }
      
      setFormData({
        date: data.visit_date || new Date().toISOString().split('T')[0],
        symptoms: data.symptoms || '',
        diagnosis: data.diagnosis || '',
        prescriptions: data.prescriptions || '',
        notes: data.notes || '',
        vitals: {
          bloodPressure: data.blood_pressure || '',
          temperature: data.temperature || '',
          weight: data.weight || '',
          height: data.height || ''
        },
        // Ensure treatments is an array. Backend getById returns them.
        treatments: Array.isArray(data.treatments) 
          ? data.treatments.map(t => ({ 
              name: t.treatment_name || t.name, 
              fee: t.fee 
            })) 
          : []
      });
    } catch (err) {
      console.error("Failed to load visit:", err);
      setError("Failed to load visit data.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVitalChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      vitals: {
        ...prev.vitals,
        [name]: value
      }
    }));
  };

  // Treatment Handlers
  const addTreatment = () => {
    setFormData(prev => ({
      ...prev,
      treatments: [...prev.treatments, { name: '', fee: '' }]
    }));
  };

  const removeTreatment = (index) => {
    setFormData(prev => ({
      ...prev,
      treatments: prev.treatments.filter((_, i) => i !== index)
    }));
  };

  const handleTreatmentChange = (index, field, value) => {
    const newTreatments = [...formData.treatments];
    newTreatments[index][field] = value;
    setFormData(prev => ({ ...prev, treatments: newTreatments }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // API call to update
      await apiService.updateVisit(visitId, formData);
      alert('Visit updated successfully!');
      navigate(`/patient/${patientId}`); // Go back to profile
    } catch (err) {
      console.error("Update failed:", err);
      setError(err.message || "Failed to update visit");
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center p-12"><Loader className="animate-spin h-8 w-8 text-blue-600" /></div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Visit Record</h1>
        <Link to={`/patient/${patientId}`} className="text-gray-600 hover:text-gray-900 flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Patient
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" /> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Basic Info Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4 text-blue-800">
            <Calendar className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">Visit Details</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Visit Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                required
              />
            </div>
          </div>
        </div>

        {/* Vitals Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4 text-blue-800">
            <Activity className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">Vitals</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500">BP (mmHg)</label>
              <input type="text" name="bloodPressure" placeholder="120/80" value={formData.vitals.bloodPressure} onChange={handleVitalChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500">Temp (°C)</label>
              <input type="number" step="0.1" name="temperature" value={formData.vitals.temperature} onChange={handleVitalChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500">Weight (kg)</label>
              <input type="number" step="0.1" name="weight" value={formData.vitals.weight} onChange={handleVitalChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500">Height (cm)</label>
              <input type="number" name="height" value={formData.vitals.height} onChange={handleVitalChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
          </div>
        </div>

        {/* Clinical Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-4 text-blue-800">
            <FileText className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">Clinical Findings</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Symptoms</label>
              <textarea name="symptoms" rows="2" value={formData.symptoms} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Diagnosis</label>
              <textarea name="diagnosis" rows="2" value={formData.diagnosis} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Prescriptions</label>
              <textarea name="prescriptions" rows="3" value={formData.prescriptions} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea name="notes" rows="2" value={formData.notes} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border" />
            </div>
          </div>
        </div>

        {/* Treatments */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center text-blue-800">
              <Pill className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">Treatments & Fees</h2>
            </div>
            <button type="button" onClick={addTreatment} className="flex items-center text-sm text-blue-600 hover:text-blue-800">
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </button>
          </div>
          
          <div className="space-y-3">
            {formData.treatments.map((treatment, index) => (
              <div key={index} className="flex gap-3 items-end">
                <div className="flex-grow">
                  <label className="block text-xs text-gray-500">Treatment Name</label>
                  <input
                    type="text"
                    value={treatment.name}
                    onChange={(e) => handleTreatmentChange(index, 'name', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-xs text-gray-500">Fee (MMK)</label>
                  <input
                    type="number"
                    value={treatment.fee}
                    onChange={(e) => handleTreatmentChange(index, 'fee', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeTreatment(index)}
                  className="mb-2 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
            {formData.treatments.length === 0 && (
              <p className="text-gray-500 text-sm italic text-center py-2">No treatments added.</p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/patient/${patientId}`)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : (
              <>
                <Save className="h-4 w-4 mr-2" /> Update Visit
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VisitEditForm;